import api from "./api";
import {
  PackingList,
  PackingDetail,
  PackingListFilter,
  ApiResponse,
} from "../types";

export interface CreatePackingListData {
  description?: string;
  styleNumber: string;
  poNumber: string;
}

export interface UpdatePackingListData {
  description?: string;
  status?: "unpacked" | "packing" | "completed" | "unpacked_partial";
}

export interface PackingStatistics {
  totalLists: number;
  unpackedLists: number;
  packingLists: number;
  completedLists: number;
  totalBoxes: number;
  listsWithMissing?: number;
  listsWithExtra?: number;
  listsWithOffList?: number;
}

export interface ImportResult {
  success: boolean;
  message: string;
  packingListId?: string;
  poNumber: string;
  styleNumber: string;
  totalBoxes: number;
  totalItems: number;
  successfulMatches: number;
  missingSkus: number;
  missingSkuDetails: Array<{
    styleNumber: string;
    colorCode: string;
    colorDescription: string;
    size: string;
    normalizedSize: string;
    quantity: number;
    boxNumber: number;
  }>;
}

export interface ExportParams {
  type: "planned" | "actual" | "comparison";
  packingListIds?: string[];
  styleNumber?: string;
  poNumber?: string;
  dateFrom?: string;
  dateTo?: string;
}

/** 计划明细逐行预览结果（条码由后端按 款号+颜色码+尺码 反查得出） */
export interface PlanPreviewRow {
  size: string;
  quantity: number;
  barcode: string | null;
  found: boolean;
}

export interface PlanPreviewResult {
  styleNumber: string;
  colorCode: string;
  rows: PlanPreviewRow[];
  missing: Array<{ size: string; quantity: number }>;
}

/** 计划明细行（前端编辑用） */
export interface PlanItemDraft {
  size: string;
  quantity: number;
  barcode: string | null;
  found: boolean;
}

class PackingService {
  // 装箱清单管理
  async getPackingLists(filter?: PackingListFilter): Promise<PackingList[]> {
    // 🚀 性能优化：支持新的分页响应格式
    // 使用带异常信息的接口（专门为Web端提供）
    const response = await api.get<ApiResponse<{ items: PackingList[]; total: number; limit: number; offset: number; hasMore: boolean }>>(
      "/packing/lists-with-anomalies",
      {
        params: { ...filter, limit: 1000 }, // 请求足够多的数据以兼容现有功能
      },
    );
    // 新格式：response.data.data 是 { items: [], total: 63 }
    // 返回 items 数组（包含异常信息）
    return response.data.data?.items || [];
  }

  async getPackingList(id: string): Promise<PackingList | null> {
    try {
      const response = await api.get<ApiResponse<PackingList>>(
        `/packing/lists/${id}`,
      );
      return response.data.data || null;
    } catch (error) {
      return null;
    }
  }

  async createPackingList(data: CreatePackingListData): Promise<PackingList> {
    const response = await api.post<ApiResponse<PackingList>>(
      "/packing/lists",
      data,
    );
    return response.data.data!;
  }

  async updatePackingList(
    id: string,
    data: UpdatePackingListData,
  ): Promise<PackingList> {
    const response = await api.put<ApiResponse<PackingList>>(
      `/packing/lists/${id}`,
      data,
    );
    return response.data.data!;
  }

  async deletePackingList(id: string): Promise<void> {
    await api.delete(`/packing/lists/${id}`);
  }

  async getPackingStatistics(): Promise<PackingStatistics> {
    const response = await api.get<ApiResponse<any>>("/packing/statistics");
    const data = response.data.data!;
    // 映射后端字段名到前端字段名
    return {
      totalLists: data.total || 0,
      unpackedLists: data.unpacked || 0,
      packingLists: data.packing || 0,
      completedLists: data.completed || 0,
      totalBoxes: data.totalBoxes || 0,
    };
  }

  // 装箱明细管理
  async getPackingDetails(listId: string): Promise<PackingDetail[]> {
    const response = await api.get<ApiResponse<PackingDetail[]>>(
      `/packing/lists/${listId}/details`,
    );
    return response.data.data || [];
  }

  async getPackingDetail(id: string): Promise<PackingDetail | null> {
    try {
      const response = await api.get<ApiResponse<PackingDetail>>(
        `/packing/details/${id}`,
      );
      return response.data.data || null;
    } catch (error) {
      return null;
    }
  }

  async createPackingDetail(data: any): Promise<PackingDetail> {
    const response = await api.post<ApiResponse<PackingDetail>>(
      "/packing/details",
      data,
    );
    return response.data.data!;
  }

  async updatePackingDetail(id: string, data: any): Promise<PackingDetail> {
    const response = await api.put<ApiResponse<PackingDetail>>(
      `/packing/details/${id}`,
      data,
    );
    return response.data.data!;
  }

  async deletePackingDetail(id: string): Promise<void> {
    await api.delete(`/packing/details/${id}`);
  }

  /**
   * 预览计划明细的 SKU 匹配结果（不落库）。
   * 用于在编辑弹窗内实时显示每个尺码对应的条码，并标出匹配不到 SKU 的尺码。
   */
  async resolvePlanPreview(
    packingListId: string,
    colorCode: string,
    items: Array<{ size: string; quantity: number }>,
  ): Promise<PlanPreviewResult | null> {
    const response = await api.post<ApiResponse<PlanPreviewResult>>(
      "/packing/details/resolve-preview",
      { packingListId, colorCode, items },
    );
    return response.data.data || null;
  }

  // 导入导出功能
  async importPackingPlan(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post<ImportResult>("/packing/import", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 300000, // 5分钟超时，支持大量箱子和多Sheet导入
    });
    return response.data;
  }

  async importPackingTemplate(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post<ImportResult>(
      "/packing/import-template",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 300000, // 5分钟超时，用于处理大量箱子的导入
      },
    );
    return response.data;
  }

  async exportPackingData(params: ExportParams): Promise<Blob> {
    const response = await api.get("/packing/export", {
      params,
      responseType: "blob",
      timeout: 300000, // 5分钟超时，支持大量装箱数据导出
    });
    return response.data;
  }

  async exportSelectedPackingData(packingListIds: string[]): Promise<Blob> {
    const response = await api.post(
      "/packing/export-selected",
      {
        packingListIds,
      },
      {
        responseType: "blob",
        timeout: 300000, // 5分钟超时，支持大量装箱清单导出
      },
    );
    return response.data;
  }

  async exportSelectedPackingDataEAN(packingListIds: string[]): Promise<Blob> {
    const response = await api.post(
      "/packing/export-selected-ean",
      {
        packingListIds,
      },
      {
        responseType: "blob",
        timeout: 300000, // 5分钟超时，支持大量装箱清单EAN导出
      },
    );
    return response.data;
  }
}

export const packingService = new PackingService();
export default packingService;
